import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BankIcon,
  CashSpecialIcon,
  WalletIcon,
} from "lola-framework-ui-test";
import { Popup } from "../Popup";
import { PayoutInfo } from "../PayoutInfo";
import {
  SelectPayoutMethodSlot,
  type FlatPayoutPick,
} from "../request/SelectPayoutMethodSlot";
import {
  PayoutDetailsSlot,
  type PayoutDetailsResult,
} from "../request/PayoutDetailsSlot";
import { useGalleryPickerLoading } from "../../hooks/useGalleryPickerLoading";
import { useGalleryStore } from "../../gallery/galleryStore";
import { useStore } from "../../store/globalState";
import {
  useComposeStore,
  type ComposeSelection,
} from "../../store/useComposeStore";
import { PayoutMethodType } from "../../interfaces/payout";
import type { IUserData } from "../../shared/session/interfaces/session";
import type { IBankAccountPaymentMethod } from "../../interfaces/payment-method";
import { claimDefaultPayoutSelection } from "../../utils/claimPayoutCompose";
import { defaultPayoutPickerTitle } from "../../utils/fundRequestPayoutDisplay";
import {
  mockCatalogFor,
  requestAvailablePayoutMethods,
  requestDestinationCards,
  requestEstimateOptions,
  requestSavedBankAccounts,
} from "../../gallery/requestMock";
import { offerableSavedBankAccounts } from "../../utils/offerableBankAccounts";
import { payerCodesEquivalent } from "../../utils/payerCode";

type PayoutStep = "method" | "details";

export interface FundRequestPayoutPopupsProps {
  visible: boolean;
  onClose: () => void;
  destinationCountry: string;
  userData: IUserData | undefined;
  onSelectionChange: () => void;
}

export function FundRequestPayoutPopups({
  visible,
  onClose,
  destinationCountry,
  userData,
  onSelectionChange,
}: FundRequestPayoutPopupsProps) {
  const { t } = useTranslation("fund-request");
  const { theme } = useStore();
  const { bodyCopy } = theme.whiteView;
  const savedListsUi = useGalleryStore((s) => s.savedListsUi);

  const selection = useComposeStore((s) => s.selection);
  const setSelection = useComposeStore((s) => s.setSelection);

  const [step, setStep] = useState<PayoutStep>("method");
  const [pendingMethod, setPendingMethod] = useState<PayoutMethodType | null>(
    null,
  );

  const pickerLoading = useGalleryPickerLoading(visible && step === "method");
  const habitsLoadDelay = useGalleryPickerLoading(
    savedListsUi !== "empty" &&
      visible &&
      step === "details" &&
      pendingMethod === PayoutMethodType.CASH_PICKUP,
  );

  const defaultSelection = useMemo(
    () => claimDefaultPayoutSelection(userData),
    [userData],
  );

  const destinationCards = useMemo(
    () => requestDestinationCards.filter((c) => c.enabled !== false),
    [],
  );
  const bankAvailable = requestAvailablePayoutMethods.includes(
    PayoutMethodType.BANK_ACCOUNT,
  );
  const bankMethodOptions = useMemo(
    () =>
      requestEstimateOptions.filter(
        (o) => o.payoutMethod === PayoutMethodType.BANK_ACCOUNT,
      ),
    [],
  );
  const catalogBankPayerCodes = useMemo(
    () => mockCatalogFor(destinationCountry).banks.map((b) => b.id),
    [destinationCountry],
  );
  const vaultAccounts = useMemo(
    () =>
      requestSavedBankAccounts.filter(
        (a) =>
          (a.countryCode || "").toUpperCase() ===
          destinationCountry.toUpperCase(),
      ),
    [destinationCountry],
  );
  const offerableAccounts = useMemo(
    () =>
      offerableSavedBankAccounts(
        vaultAccounts,
        bankMethodOptions,
        catalogBankPayerCodes,
      ),
    [vaultAccounts, bankMethodOptions, catalogBankPayerCodes],
  );

  const bankLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const bank of mockCatalogFor(destinationCountry).banks) {
      map[bank.id.toUpperCase()] = bank.name;
    }
    return map;
  }, [destinationCountry]);

  const defaultRowIcon = useMemo(() => {
    const pm = userData?.paymentMethod?.type;
    switch (pm) {
      case "cash":
        return <CashSpecialIcon size={22} colors={[bodyCopy, bodyCopy]} />;
      case "card":
        return <WalletIcon size={22} colors={[bodyCopy, bodyCopy]} />;
      case "bank_account":
        return <BankIcon size={22} colors={[bodyCopy, bodyCopy]} />;
      default:
        return <WalletIcon size={22} colors={[bodyCopy, bodyCopy]} />;
    }
  }, [bodyCopy, userData?.paymentMethod?.type]);

  const applyDefaultRow = useCallback(() => {
    if (!defaultSelection) return;
    setSelection(defaultSelection);
    onSelectionChange();
    onClose();
    setStep("method");
    setPendingMethod(null);
  }, [defaultSelection, onClose, onSelectionChange, setSelection]);

  const persistSelection = useCallback(
    (next: ComposeSelection) => {
      setSelection(next);
      onSelectionChange();
      onClose();
      setStep("method");
      setPendingMethod(null);
    },
    [onClose, onSelectionChange, setSelection],
  );

  const handleSelectCard = useCallback(
    (picked: {
      paymentMethodId: string;
      brand: string;
      last4: string;
      cardExpiry?: string;
      cardholderName?: string;
    }) => {
      const option =
        requestEstimateOptions.find(
          (o) => o.payoutMethod === PayoutMethodType.PUSH_TO_CARD,
        ) ?? null;
      persistSelection({
        method: PayoutMethodType.PUSH_TO_CARD,
        option,
        payout: {
          source: "saved",
          paymentMethodId: picked.paymentMethodId,
          brand: picked.brand,
          last4: picked.last4,
          cardExpiry: picked.cardExpiry,
          cardholderName: picked.cardholderName,
        },
      });
    },
    [persistSelection],
  );

  const handleSelectBank = useCallback(
    (account: IBankAccountPaymentMethod) => {
      const bankCode = (account.bankName || "").toUpperCase();
      const option =
        bankMethodOptions.find((o) =>
          payerCodesEquivalent(o.payerCode, bankCode),
        ) ??
        bankMethodOptions[0] ??
        null;
      persistSelection({
        method: PayoutMethodType.BANK_ACCOUNT,
        option,
        payout: {
          source: "saved",
          paymentMethodId: account.paymentMethodId,
          bankId: bankCode,
          last4: account.maskedAccountNumber.replace(/\D/g, "").slice(-4),
        },
      });
    },
    [bankMethodOptions, persistSelection],
  );

  const handlePayoutPick = useCallback(
    (pick: FlatPayoutPick) => {
      if (pick.kind === "card") {
        handleSelectCard(pick.card);
        return;
      }
      if (pick.kind === "bank") {
        handleSelectBank(pick.account);
        return;
      }
      if (pick.kind === "add" || pick.kind === "addBank") {
        setPendingMethod(
          pick.kind === "addBank"
            ? PayoutMethodType.BANK_ACCOUNT
            : PayoutMethodType.PUSH_TO_CARD,
        );
        setStep("details");
        return;
      }
      setPendingMethod(pick.method);
      setStep("details");
    },
    [handleSelectBank, handleSelectCard],
  );

  const handleDetailsSubmit = useCallback(
    (result: PayoutDetailsResult) => {
      if (!pendingMethod) return;
      persistSelection({
        method: pendingMethod,
        option: result.option,
        payout: result.payout,
      });
    },
    [pendingMethod, persistSelection],
  );

  const defaultRow =
    defaultSelection != null ? (
      <PayoutInfo
        title={defaultPayoutPickerTitle(userData, t)}
        subtitle={t("payout.default", { defaultValue: "Default" })}
        icon={defaultRowIcon}
        className="funding-neutral-icon"
        onChange={applyDefaultRow}
      />
    ) : null;

  if (!visible) return null;

  return (
    <Popup
      visible
      onClose={() => {
        setStep("method");
        setPendingMethod(null);
        onClose();
      }}
    >
      {step === "method" ? (
        <SelectPayoutMethodSlot
          selectedMethod={selection?.method ?? null}
          selectedPaymentMethodId={
            selection?.method === PayoutMethodType.PUSH_TO_CARD ||
            selection?.method === PayoutMethodType.BANK_ACCOUNT
              ? (selection.payout.paymentMethodId ?? null)
              : null
          }
          available={requestAvailablePayoutMethods}
          cards={destinationCards}
          accounts={bankAvailable ? offerableAccounts : []}
          loading={pickerLoading}
          bankLabels={bankLabels}
          prependRows={defaultRow ?? undefined}
          onPick={handlePayoutPick}
          onClose={onClose}
        />
      ) : pendingMethod ? (
        <PayoutDetailsSlot
          method={pendingMethod}
          destinationCountry={destinationCountry}
          options={requestEstimateOptions}
          value={
            selection && selection.method === pendingMethod
              ? selection.payout
              : null
          }
          habitsBusy={savedListsUi === "loading" || habitsLoadDelay}
          habitsEmpty={savedListsUi === "empty"}
          onBack={() => setStep("method")}
          onClose={onClose}
          onSubmit={handleDetailsSubmit}
        />
      ) : (
        <section className="select-payer-slot" aria-hidden="true" />
      )}
    </Popup>
  );
}
